import { Link } from '@/lib/nav';
import { ArrowRight } from 'lucide-react';
import { Container, Section, Eyebrow } from '@/components/primitives/Container';
import { LinkButton } from '@/components/primitives/Button';
import { QuestionDemo } from '@/components/demo/QuestionDemo';
import { ArgentinaMap } from '@/components/map/ArgentinaMap';
import { LayerDiagram } from '@/components/diagrams/LayerDiagram';
import { AUDIENCES } from '@/data/audiences';
import { ROUTES } from '@/lib/routes';

export function Home() {
  return (
    <>
      {/* 4.1.1 Hero */}
      <section className="rule-bottom">
        <Container className="pt-16 pb-20 md:pt-24 md:pb-28">
          <Eyebrow className="mb-8">SICA · Sistema Inteligente Curricular Argentino</Eyebrow>
          <h1 className="max-w-5xl font-display text-5xl font-medium leading-[1] tracking-tight text-accent md:text-6xl lg:text-7xl">
            El currículum argentino,
            <br />
            <span className="font-normal italic">legible y citable.</span>
          </h1>
          <div className="mt-10 grid items-end gap-10 md:grid-cols-[1fr_auto] md:gap-16">
            <p className="max-w-2xl text-lg leading-relaxed text-ink-muted md:text-xl">
              SICA es una <strong className="font-medium text-ink">inteligencia curricular validada</strong>.
              Lee los Núcleos de Aprendizajes Prioritarios y los 24 diseños jurisdiccionales argentinos
              con cita exacta a la fuente. Construida por Estrada.
            </p>
            <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
              <LinkButton href="#demo" variant="institutional" size="xl">
                Ver SICA en acción
                <ArrowRight size={16} />
              </LinkButton>
              <LinkButton href={ROUTES.contacto} variant="institutionalOutline" size="xl">
                Hablar con nosotros
              </LinkButton>
            </div>
          </div>
        </Container>
      </section>

      {/* 4.1.2 Demo */}
      <section id="demo" className="rule-bottom">
        <Container className="py-20 md:py-28">
          <div className="mb-12 grid gap-10 lg:grid-cols-[1fr_2fr] lg:gap-16">
            <div>
              <Eyebrow n="01" className="mb-4">Demostración</Eyebrow>
              <h2 className="font-display text-3xl font-medium leading-[1.05] text-accent md:text-5xl">
                Hacé una pregunta. SICA te responde con la cita exacta.
              </h2>
            </div>
            <p className="max-w-prose self-end text-lg leading-relaxed text-ink-muted">
              Si SICA no encuentra fuente, te lo dice. No inventa. Toda respuesta apunta a un documento
              normativo y a su página exacta.
            </p>
          </div>

          <QuestionDemo />

          <div className="mt-10">
            <Link
              to={ROUTES.producto.index}
              className="focus-ring inline-flex items-center gap-2 text-accent underline decoration-1 underline-offset-[6px] hover:decoration-2"
            >
              Ver más ejemplos
              <ArrowRight size={14} />
            </Link>
          </div>
        </Container>
      </section>

      {/* 4.1.3 Diferencial federal */}
      <section className="rule-bottom bg-paper-tinted/40">
        <Container className="py-20 md:py-28">
          <div className="mb-14 grid gap-10 lg:grid-cols-[1fr_2fr] lg:gap-16">
            <div>
              <Eyebrow n="02" className="mb-4">Diferencial federal</Eyebrow>
              <h2 className="font-display text-3xl font-medium leading-[1.05] text-accent md:text-5xl">
                24 jurisdicciones. Un piso común. Lectura comparable.
              </h2>
            </div>
            <div className="max-w-prose space-y-4 self-end text-ink-muted">
              <p className="text-lg leading-relaxed">
                El sistema educativo argentino es federal: NAP a nivel nacional, diseños jurisdiccionales
                por provincia.
              </p>
              <p className="text-lg leading-relaxed">
                SICA preserva esa jerarquía. <strong className="font-medium text-ink">No la colapsa.</strong>
              </p>
            </div>
          </div>

          <ArgentinaMap />

          <div className="mt-10">
            <Link
              to={ROUTES.producto.jurisdicciones}
              className="focus-ring inline-flex items-center gap-2 text-accent underline decoration-1 underline-offset-[6px] hover:decoration-2"
            >
              Explorar el mapa de cobertura
              <ArrowRight size={14} />
            </Link>
          </div>
        </Container>
      </section>

      {/* 4.1.4 Método */}
      <section className="rule-bottom">
        <Container className="py-20 md:py-28">
          <div className="mb-14 max-w-3xl">
            <Eyebrow n="03" className="mb-4">Método y trazabilidad</Eyebrow>
            <h2 className="font-display text-3xl font-medium leading-[1.05] text-accent md:text-5xl">
              Cada respuesta tiene página y documento.
              <br />
              <span className="font-normal italic">Cero alucinaciones sobre la norma.</span>
            </h2>
          </div>

          <LayerDiagram />

          <div className="mt-10">
            <Link
              to={ROUTES.producto.metodo}
              className="focus-ring inline-flex items-center gap-2 text-accent underline decoration-1 underline-offset-[6px] hover:decoration-2"
            >
              Conocer el método
              <ArrowRight size={14} />
            </Link>
          </div>
        </Container>
      </section>

      {/* 4.1.5 Cuatro audiencias — dark institutional */}
      <section className="rule-bottom bg-accent text-accent-contrast">
        <Container className="py-20 md:py-28">
          <div className="mb-14 max-w-3xl">
            <p className="mb-4 text-[0.7rem] font-medium uppercase tracking-[0.18em] text-accent-contrast/60">
              <span className="text-accent-contrast">04</span>
              <span className="mx-1.5 text-accent-contrast/40" aria-hidden="true">·</span>
              <span>Cuatro audiencias</span>
            </p>
            <h2 className="font-display text-3xl font-medium leading-[1.05] md:text-5xl">
              SICA es infraestructura. Esto significa cosas distintas según quién la usa.
            </h2>
          </div>

          <div className="grid gap-px border border-accent-contrast/15 bg-accent-contrast/15 md:grid-cols-2">
            {AUDIENCES.map((a, i) => (
              <Link
                key={a.id}
                to={a.href}
                className="group bg-accent p-8 transition-colors hover:bg-accent-contrast/5 md:p-10"
              >
                <div className="mb-6 flex items-start justify-between">
                  <span className="font-mono text-xs text-accent-contrast/60">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <ArrowRight
                    size={16}
                    className="text-accent-contrast/60 transition-all group-hover:translate-x-1 group-hover:text-accent-contrast"
                  />
                </div>
                <h3 className="mb-4 font-display text-3xl font-medium md:text-4xl">{a.label}</h3>
                <p className="mb-6 text-lg leading-relaxed text-accent-contrast/70">{a.task}</p>
                <span className="text-sm underline decoration-1 underline-offset-4">{a.cta}</span>
              </Link>
            ))}
          </div>
        </Container>
      </section>

      {/* 4.1.6 Construida por Estrada */}
      <Section ariaLabelledby="estrada-title">
        <Container width="editorial">
          <Eyebrow className="mb-4">Editorial responsable</Eyebrow>
          <h2
            id="estrada-title"
            className="font-display text-2xl font-normal leading-snug tracking-tight text-accent md:text-3xl"
          >
            Estrada lleva más de 150 años construyendo soportes para que la escuela argentina funcione.
            SICA es el siguiente.
          </h2>
          <p className="mt-4 text-sm text-ink-muted">Editorial Estrada · 1869.</p>
          <div className="mt-6">
            <Link
              to={ROUTES.sobre}
              className="focus-ring inline-flex items-center gap-2 text-accent underline decoration-1 underline-offset-[6px] hover:decoration-2"
            >
              Conocer más
              <ArrowRight size={14} />
            </Link>
          </div>
        </Container>
      </Section>
    </>
  );
}
